import { Component } from '@angular/core';
import { AbstractFieldDirective } from '../abstract-field.directive';
import type { DateValue } from '../../model/date-value.type';
import { toDateValue } from '../../tool/field.function';

@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss']
})
export class DateFieldComponent extends AbstractFieldDirective {

  // caches avoid returning a new Date instance on every change detection cycle,
  // which otherwise retriggers the datepicker (infinite update loop / freeze)
  private readonly valueCache = new ParsedDateCache();
  private readonly minDateCache = new ParsedDateCache();
  private readonly maxDateCache = new ParsedDateCache();

  protected get value(): undefined | Date {
    return this.valueCache.parse(this.field?.value);
  }

  protected get minDate(): undefined | Date {
    return this.minDateCache.parse(this.field?.minValue);
  }
  protected get maxDate(): undefined | Date {
    return this.maxDateCache.parse(this.field?.maxValue);
  }

  protected updateValue(value?: null | string): void {
    if (!this.field)
      return;
    const newValue = toDateValue(value);
    if (this.field.value === newValue)
      return;
    this.field.value = newValue;
    this.fieldChange.emit(this.field);
  }

  protected updateMinValue(value?: null | DateValue): void {
    if (!this.field)
      return;
    const minValue = toDateValue(value);
    if (this.field.minValue === minValue)
      return;
    this.field.minValue = minValue;
    this.fieldChange.emit(this.field);
  }
  protected updateMaxValue(value?: null | DateValue): void {
    if (!this.field)
      return;
    const maxValue = toDateValue(value);
    if (this.field.maxValue === maxValue)
      return;
    this.field.maxValue = maxValue;
    this.fieldChange.emit(this.field);
  }

  protected get error(): string {
    if (this.value === undefined)
      return '';
    if (!this.value && !this.required)
      return '';
    if (this.minDate !== undefined && this.value < this.minDate)
      return `La date doit être après le ${this.minDate.toLocaleDateString()} inclus.`;
    if (this.maxDate !== undefined && this.value > this.maxDate)
      return `La date doit être avant le ${this.maxDate.toLocaleDateString()} inclus.`;
    return '';
  }

}

class ParsedDateCache {
  private rawValue?: null | string;
  private date?: Date;

  parse(value?: null | string): undefined | Date {
    if (value !== this.rawValue) {
      this.rawValue = value;
      this.date = value ? new Date(value) : undefined;
    }
    return this.date;
  }
}
